import type {
  AnnualTaxAdjustment,
  AnnualTaxSettings,
  AnnualTaxSummary,
  AuditNote,
  BasPeriod,
  BasSummary,
  Expense,
  ExpenseCategory,
  FinancialYearPeriod,
  IncomeSummary,
  MonthlyFinancialSummary,
  SourceDocument,
  VehicleExpenseMethod,
} from "@/types/tax";
import { clampPercent } from "@/lib/utils";
import { expenseCategoryLabels, gstBlockedReasons } from "@/data/atoRules";

export const TAX_INVOICE_THRESHOLD = 82.5;
export const DEFAULT_CENTS_PER_KM_RATE = 0.88;
export const BUSINESS_KM_CAP = 5000;
export const VEHICLE_RUNNING_COST_CATEGORIES: ExpenseCategory[] = [
  "fuel",
  "ev_charging",
  "registration",
  "insurance",
  "service_repairs",
  "tyres",
  "car_wash",
  "lease_rental",
];

const VEHICLE_RUNNING_COST_SET = new Set<ExpenseCategory>(VEHICLE_RUNNING_COST_CATEGORIES);

export function roundMoney(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function gstFromGross(gross: number) {
  return roundMoney(gross / 11);
}

export function businessPortion(amount: number, businessUsePercent: number) {
  return roundMoney(amount * (clampPercent(businessUsePercent) / 100));
}

export function expenseGstComponent(expense: Expense) {
  if (expense.gstTreatment === "manual_gst" || expense.gstTreatment === "mixed_invoice") {
    return roundMoney(expense.manualGstAmount || 0);
  }

  if (expense.gstTreatment === "gst_included") {
    return gstFromGross(expense.amount);
  }

  return 0;
}

export function claimableGstForExpense(expense: Expense) {
  const gstComponent = expenseGstComponent(expense);
  if (gstComponent <= 0) return 0;
  if (expense.amount > TAX_INVOICE_THRESHOLD && !expense.hasTaxInvoice) return 0;
  return businessPortion(gstComponent, expense.businessUsePercent);
}

function claimableGstForExpenseAtPercent(expense: Expense, businessUsePercent: number) {
  const gstComponent = expenseGstComponent(expense);
  if (gstComponent <= 0) return 0;
  if (expense.amount > TAX_INVOICE_THRESHOLD && !expense.hasTaxInvoice) return 0;
  return businessPortion(gstComponent, businessUsePercent);
}

export function auditExpense(expense: Expense): AuditNote[] {
  const notes: AuditNote[] = [];
  const blockedReason = gstBlockedReasons[expense.gstTreatment];

  if (blockedReason) {
    notes.push({
      id: `audit-${expense.id}-blocked`,
      severity: "info",
      title: `${expenseCategoryLabels[expense.category]} has no GST credit`,
      message: blockedReason,
      sourceId: expense.id,
    });
  }

  if (expense.amount > TAX_INVOICE_THRESHOLD && !expense.hasTaxInvoice && expenseGstComponent(expense) > 0) {
    notes.push({
      id: `audit-${expense.id}-invoice`,
      severity: "danger",
      title: "Tax invoice required",
      message: `${expenseCategoryLabels[expense.category]} is over A$82.50. Add a valid tax invoice before claiming the GST credit.`,
      sourceId: expense.id,
    });
  }

  if (expense.businessUsePercent < 100 && expenseGstComponent(expense) > 0) {
    notes.push({
      id: `audit-${expense.id}-apportion`,
      severity: "warning",
      title: "Business-use apportionment applied",
      message: `${expense.businessUsePercent}% of the GST is included for rideshare business use.`,
      sourceId: expense.id,
    });
  }

  if (expense.category === "car_purchase") {
    notes.push({
      id: `audit-${expense.id}-car-limit`,
      severity: "warning",
      title: "Review car purchase rules",
      message: "Car cost limit, luxury car tax, and GST registration timing rules may affect this credit.",
      sourceId: expense.id,
    });
  }

  if (expense.category === "driver_accreditation") {
    notes.push({
      id: `audit-${expense.id}-licence`,
      severity: "warning",
      title: "Private licence costs are private",
      message: "Commercial approvals may be business expenses, but private driver licence costs are not claimable.",
      sourceId: expense.id,
    });
  }

  return notes;
}

export function getBasPeriodForDate(dateLike: string | Date): BasPeriod {
  const date = typeof dateLike === "string" ? new Date(`${dateLike}T00:00:00`) : dateLike;
  const month = date.getMonth();
  const year = date.getFullYear();

  if (month >= 6 && month <= 8) {
    return makeQuarter(year, 6, 8, "Q1", `${year}-${String(year + 1).slice(2)}`, `${year}-10-28`);
  }
  if (month >= 9 && month <= 11) {
    return makeQuarter(year, 9, 11, "Q2", `${year}-${String(year + 1).slice(2)}`, `${year + 1}-02-28`);
  }
  if (month >= 0 && month <= 2) {
    return makeQuarter(year, 0, 2, "Q3", `${year - 1}-${String(year).slice(2)}`, `${year}-04-28`);
  }
  return makeQuarter(year, 3, 5, "Q4", `${year - 1}-${String(year).slice(2)}`, `${year}-07-28`);
}

function makeQuarter(
  year: number,
  startMonth: number,
  endMonth: number,
  quarter: "Q1" | "Q2" | "Q3" | "Q4",
  financialYear: string,
  dueDate: string,
): BasPeriod {
  const startDate = new Date(year, startMonth, 1);
  const endDate = new Date(year, endMonth + 1, 0);
  const label = `${quarter} FY${financialYear}`;
  return {
    id: `${financialYear}-${quarter}`,
    label,
    startDate: toIsoDate(startDate),
    endDate: toIsoDate(endDate),
    dueDate,
    financialYear,
  };
}

export function toIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function isDateInPeriod(date: string, period: BasPeriod) {
  return date >= period.startDate && date <= period.endDate;
}

export function documentPeriodDate(document: SourceDocument) {
  return document.extraction?.periodEnd || document.uploadedAt.slice(0, 10);
}

export function incomePeriodDate(income: IncomeSummary) {
  return income.periodEnd || income.periodStart;
}

export function incomeSummaryFromDocument(document: SourceDocument): IncomeSummary | null {
  if (document.status !== "accepted" || !document.extraction) return null;
  return {
    id: `inc-${document.id}`,
    userId: document.userId,
    provider: document.extraction.provider,
    periodStart: document.extraction.periodStart,
    periodEnd: document.extraction.periodEnd,
    grossIncome: document.extraction.grossFares,
    platformFeesGross: document.extraction.platformFeesGross,
    platformFeesGst: document.extraction.platformFeesGst,
    tollsGross: document.extraction.tollsGross,
    tipsAndBonuses: document.extraction.tipsAndBonuses,
    sourceType: document.sourceType === "manual_entry" ? "manual" : "document_scan",
    sourceDocumentId: document.id,
    confidence: document.extraction.confidence,
    notes: document.extraction.notes.join("\n"),
  };
}

export function summarizeBas(
  period: BasPeriod,
  incomeSummaries: IncomeSummary[],
  expenses: Expense[],
  documents: SourceDocument[] = [],
): BasSummary {
  const periodIncome = incomeSummaries.filter((income) => isDateInPeriod(incomePeriodDate(income), period));
  const periodExpenses = expenses.filter((expense) => isDateInPeriod(expense.date, period));
  const sourceDocumentIds = new Set(periodIncome.map((income) => income.sourceDocumentId).filter(Boolean));
  const periodDocuments = documents.filter((document) => sourceDocumentIds.has(document.id));

  const sourceSales = roundMoney(periodIncome.reduce((sum, item) => sum + item.grossIncome, 0));
  const sourcePlatformCredits = roundMoney(
    periodIncome.reduce((sum, item) => sum + (item.platformFeesGst || 0), 0),
  );
  const manualExpenseCredits = roundMoney(periodExpenses.reduce((sum, expense) => sum + claimableGstForExpense(expense), 0));
  const gstOnSales1A = gstFromGross(sourceSales);
  const gstOnPurchases1B = roundMoney(sourcePlatformCredits + manualExpenseCredits);
  const auditNotes = [
    ...periodExpenses.flatMap(auditExpense),
    ...periodIncome.flatMap((income) =>
      income.confidence === "low"
        ? [
            {
              id: `audit-${income.id}-confidence`,
              severity: "warning" as const,
              title: "Review extracted PDF figures",
              message: "One income summary has low extraction confidence. Confirm the fields before using this BAS period.",
              sourceId: income.id,
            },
          ]
        : [],
    ),
  ];

  if (sourceSales > 0) {
    auditNotes.unshift({
      id: `audit-${period.id}-full-fare`,
      severity: "info",
      title: "GST calculated on full fare",
      message: "1A uses one-eleventh of full passenger fares, before platform fees are deducted.",
    });
  }

  return {
    period,
    g1TotalSales: sourceSales,
    gstOnSales1A,
    gstOnPurchases1B,
    netGst: roundMoney(gstOnSales1A - gstOnPurchases1B),
    sourceSales,
    sourcePlatformCredits,
    manualExpenseCredits,
    incomeSummaries: periodIncome,
    documents: periodDocuments,
    expenses: periodExpenses,
    auditNotes,
  };
}

export function availableBasPeriods(incomeSummaries: IncomeSummary[], expenses: Expense[]) {
  const periodMap = new Map<string, BasPeriod>();
  for (const income of incomeSummaries) {
    const period = getBasPeriodForDate(incomePeriodDate(income));
    periodMap.set(period.id, period);
    const financialYear = makeFinancialYearPeriod(period.financialYear);
    periodMap.set(financialYear.id, financialYear);
  }
  for (const expense of expenses) {
    const period = getBasPeriodForDate(expense.date);
    periodMap.set(period.id, period);
    const financialYear = makeFinancialYearPeriod(period.financialYear);
    periodMap.set(financialYear.id, financialYear);
  }

  if (periodMap.size === 0) {
    const current = getBasPeriodForDate(new Date());
    periodMap.set(current.id, current);
    const financialYear = makeFinancialYearPeriod(current.financialYear);
    periodMap.set(financialYear.id, financialYear);
  }

  return Array.from(periodMap.values()).sort((a, b) => {
    const dateSort = b.startDate.localeCompare(a.startDate);
    if (dateSort !== 0) return dateSort;
    return a.id.includes("-FY") ? 1 : b.id.includes("-FY") ? -1 : a.label.localeCompare(b.label);
  });
}

export function financialYearForDate(dateLike: string | Date): FinancialYearPeriod {
  const date = typeof dateLike === "string" ? new Date(`${dateLike}T00:00:00`) : dateLike;
  const calendarYear = date.getFullYear();
  const month = date.getMonth();
  const startYear = month >= 6 ? calendarYear : calendarYear - 1;
  return makeFinancialYearPeriod(`${startYear}-${String(startYear + 1).slice(2)}`);
}

export function availableFinancialYears(incomeSummaries: IncomeSummary[], expenses: Expense[]) {
  const yearMap = new Map<string, FinancialYearPeriod>();
  for (const income of incomeSummaries) {
    const period = financialYearForDate(incomePeriodDate(income));
    yearMap.set(period.id, period);
  }
  for (const expense of expenses) {
    const period = financialYearForDate(expense.date);
    yearMap.set(period.id, period);
  }
  if (yearMap.size === 0) {
    const current = financialYearForDate(new Date());
    yearMap.set(current.id, current);
  }
  return Array.from(yearMap.values()).sort((a, b) => b.startDate.localeCompare(a.startDate));
}

export function defaultAnnualTaxSettings(userId: string, financialYear: string): AnnualTaxSettings {
  return {
    id: `annual-settings-${userId}-${financialYear}`,
    userId,
    financialYear,
    selectedVehicleMethod: "logbook_actual",
    businessKilometres: 0,
    logbookBusinessUsePercent: 100,
    centsPerKmRate: DEFAULT_CENTS_PER_KM_RATE,
    vehicleMakeModel: "",
    vehiclePurchaseDate: "",
    vehiclePurchasePrice: 0,
    vehiclePurchaseGstClaimed: 0,
    totalKilometres: 0,
  };
}

export function isVehicleRunningCost(category: ExpenseCategory) {
  return VEHICLE_RUNNING_COST_SET.has(category);
}

export function expenseIncomeTaxDeduction(expense: Expense, overrideBusinessUsePercent?: number) {
  const percent = overrideBusinessUsePercent ?? expense.businessUsePercent;
  const businessAmount = businessPortion(expense.amount, percent);
  return Math.max(0, roundMoney(businessAmount - claimableGstForExpenseAtPercent(expense, percent)));
}

export function vehicleExpenseBusinessUsePercent(expense: Expense) {
  return clampPercent(expense.businessUsePercent);
}

export function summarizeAnnualTax(
  period: FinancialYearPeriod,
  incomeSummaries: IncomeSummary[],
  expenses: Expense[],
  settings: AnnualTaxSettings,
  adjustments: AnnualTaxAdjustment[] = [],
): AnnualTaxSummary {
  const annualIncome = incomeSummaries.filter((income) => {
    const date = incomePeriodDate(income);
    return date >= period.startDate && date <= period.endDate;
  });
  const annualExpenses = expenses.filter((expense) => expense.date >= period.startDate && expense.date <= period.endDate);
  const annualAdjustments = adjustments.filter((adjustment) => adjustment.financialYear === period.financialYear);

  const rideshareIncome = roundMoney(annualIncome.reduce((sum, income) => sum + (income.grossIncome || 0), 0));
  const incomeGstExcluded = gstFromGross(rideshareIncome);
  const rideshareIncomeNetOfGst = roundMoney(Math.max(0, rideshareIncome - incomeGstExcluded));
  const tipsAndBonuses = roundMoney(annualIncome.reduce((sum, income) => sum + (income.tipsAndBonuses || 0), 0));
  const adjustmentIncome = roundMoney(annualAdjustments.filter((item) => item.type === "other_income").reduce((sum, item) => sum + item.amount, 0));
  const assessableIncome = roundMoney(rideshareIncomeNetOfGst + tipsAndBonuses + adjustmentIncome);

  const platformFeeDeduction = roundMoney(
    annualIncome.reduce((sum, income) => sum + Math.max(0, (income.platformFeesGross || 0) - (income.platformFeesGst || 0)), 0),
  );
  const vehicleRunningCosts = roundMoney(
    annualExpenses
      .filter((expense) => isVehicleRunningCost(expense.category))
      .reduce((sum, expense) => sum + expenseIncomeTaxDeduction(expense, vehicleExpenseBusinessUsePercent(expense)), 0),
  );
  const centsPerKmDeduction = roundMoney(Math.min(Math.max(0, settings.businessKilometres || 0), BUSINESS_KM_CAP) * (settings.centsPerKmRate || DEFAULT_CENTS_PER_KM_RATE));
  const logbookDeduction = vehicleRunningCosts;
  const recommendedMethod: VehicleExpenseMethod = centsPerKmDeduction > logbookDeduction ? "cents_per_km" : "logbook_actual";
  const selectedMethod = settings.selectedVehicleMethod || recommendedMethod;
  const selectedVehicleDeduction = selectedMethod === "cents_per_km" ? centsPerKmDeduction : logbookDeduction;

  const otherExpenseDeductions = roundMoney(
    annualExpenses
      .filter((expense) => !isVehicleRunningCost(expense.category) && expense.category !== "car_purchase")
      .reduce((sum, expense) => sum + expenseIncomeTaxDeduction(expense), 0),
  );
  const deductionAdjustments = annualAdjustments.filter((adjustment) => adjustment.type !== "other_income");
  const vehicleDepreciationAdjustments = deductionAdjustments.filter((adjustment) => adjustment.type === "car_depreciation" || adjustment.type === "instant_asset_write_off");
  const otherDeductionAdjustments = deductionAdjustments.filter((adjustment) => adjustment.type !== "car_depreciation" && adjustment.type !== "instant_asset_write_off");
  const vehicleDepreciationAmount = roundMoney(vehicleDepreciationAdjustments.reduce((sum, adjustment) => sum + Math.max(0, adjustment.amount), 0));
  const otherAnnualDeductions = roundMoney(otherDeductionAdjustments.reduce((sum, adjustment) => sum + Math.max(0, adjustment.amount), 0));
  const includedDepreciationDeductions = selectedMethod === "cents_per_km" ? 0 : vehicleDepreciationAmount;
  const adjustmentDeductions = roundMoney(includedDepreciationDeductions + otherAnnualDeductions);
  const totalDeductions = roundMoney(platformFeeDeduction + selectedVehicleDeduction + otherExpenseDeductions + adjustmentDeductions);
  const netBusinessProfit = roundMoney(assessableIncome - totalDeductions);

  const vehicleRunningCostCount = annualExpenses.filter((expense) => isVehicleRunningCost(expense.category)).length;
  const otherExpenses = annualExpenses.filter((expense) => !isVehicleRunningCost(expense.category) && expense.category !== "car_purchase");
  const carPurchaseCount = annualExpenses.filter((expense) => expense.category === "car_purchase").length;

  const deductionSummaries = [
    {
      id: "platform-fees",
      label: "Platform fees net of GST",
      amount: platformFeeDeduction,
      count: annualIncome.filter((income) => income.platformFeesGross > 0).length,
      included: true,
      note: "GST credits already claimed are excluded.",
    },
    {
      id: "vehicle-running-costs",
      label: "Vehicle running costs",
      amount: vehicleRunningCosts,
      count: vehicleRunningCostCount,
      included: selectedMethod === "logbook_actual",
      note: selectedMethod === "cents_per_km" ? "Excluded because cents/km is selected." : "Included under logbook/actual method.",
    },
    {
      id: "other-expenses",
      label: "Other business deductions",
      amount: otherExpenseDeductions,
      count: otherExpenses.length,
      included: true,
    },
    {
      id: "annual-adjustments",
      label: "Annual adjustments",
      amount: adjustmentDeductions,
      count: deductionAdjustments.length,
      included: true,
      note: selectedMethod === "cents_per_km"
        ? "Vehicle depreciation adjustments are excluded because cents/km is selected."
        : "Manual entries such as depreciation or tax-agent reviewed deductions.",
    },
    {
      id: "car-purchase-review",
      label: "Car purchase / depreciating asset",
      amount: 0,
      count: carPurchaseCount,
      included: false,
      note: carPurchaseCount > 0 ? "Needs review before any deduction is included." : "No car purchase recorded.",
    },
  ];

  const auditNotes = annualAuditNotes(settings, selectedMethod, carPurchaseCount, vehicleRunningCostCount);

  return {
    period,
    settings: { ...settings, selectedVehicleMethod: selectedMethod },
    adjustments: annualAdjustments,
    assessableIncome,
    rideshareIncome,
    incomeGstExcluded,
    tipsAndBonuses,
    platformFeeDeduction,
    vehicleRunningCosts,
    selectedVehicleDeduction,
    otherExpenseDeductions,
    adjustmentDeductions,
    vehicleDepreciationAmount,
    includedDepreciationDeductions,
    otherAnnualDeductions,
    adjustmentIncome,
    totalDeductions,
    netBusinessProfit,
    deductionSummaries,
    vehicleMethods: [
      {
        method: "cents_per_km",
        label: "Cents per km",
        deduction: centsPerKmDeduction,
        isRecommended: recommendedMethod === "cents_per_km",
        isSelected: selectedMethod === "cents_per_km",
      },
      {
        method: "logbook_actual",
        label: "Logbook / actual costs",
        deduction: logbookDeduction,
        isRecommended: recommendedMethod === "logbook_actual",
        isSelected: selectedMethod === "logbook_actual",
      },
    ],
    incomeSummaries: annualIncome,
    expenses: annualExpenses,
    auditNotes,
  };
}

function annualAuditNotes(
  settings: AnnualTaxSettings,
  selectedMethod: VehicleExpenseMethod,
  carPurchaseCount: number,
  vehicleRunningCostCount: number,
): AuditNote[] {
  const notes: AuditNote[] = [
    {
      id: "annual-tax-worksheet-only",
      severity: "info",
      title: "Income tax worksheet only",
      message: "Use these figures as a worksheet for myTax or your registered tax agent. This app does not lodge your tax return.",
    },
    {
      id: "annual-tax-gst-net",
      severity: "info",
      title: "GST excluded from tax figures",
      message: "Assessable income excludes GST on rideshare fares, and deductions exclude GST credits already claimed.",
    },
  ];

  if (selectedMethod === "cents_per_km" && vehicleRunningCostCount > 0) {
    notes.push({
      id: "annual-tax-cents-km-excludes-running-costs",
      severity: "warning",
      title: "Vehicle running costs excluded",
      message: "Fuel, charging, registration, insurance, repairs, tyres, washing, and lease/rental costs are excluded while cents per km is selected.",
    });
  }

  if (settings.businessKilometres > BUSINESS_KM_CAP) {
    notes.push({
      id: "annual-tax-business-km-cap",
      severity: "warning",
      title: "Cents per km capped",
      message: `Cents per km is capped at ${BUSINESS_KM_CAP.toLocaleString("en-AU")} business kilometres.`,
    });
  }

  if (carPurchaseCount > 0) {
    notes.push({
      id: "annual-tax-car-purchase-review",
      severity: "danger",
      title: "Car purchase needs review",
      message: "Car purchases are not automatically deducted. Add a reviewed depreciation or instant asset write-off adjustment if applicable.",
    });
  }

  return notes;
}

function makeFinancialYearPeriod(financialYear: string): FinancialYearPeriod {
  const [startYearText, endYearShortText] = financialYear.split("-");
  const startYear = Number(startYearText);
  const endYearShort = Number(endYearShortText);
  const endYear = Number.isFinite(endYearShort) ? Math.floor(startYear / 100) * 100 + endYearShort : startYear + 1;
  return {
    id: `${financialYear}-FY`,
    label: `Full FY${financialYear}`,
    startDate: `${startYear}-07-01`,
    endDate: `${endYear}-06-30`,
    dueDate: `${endYear}-10-31`,
    financialYear,
  };
}

export function monthlyFinancialSummaries(incomeSummaries: IncomeSummary[], expenses: Expense[]): MonthlyFinancialSummary[] {
  const monthMap = new Map<string, { monthStart: string; monthEnd: string; incomeSummaries: IncomeSummary[]; expenses: Expense[] }>();

  function ensureMonth(dateLike: string) {
    const date = new Date(`${dateLike || toIsoDate(new Date())}T00:00:00`);
    const monthStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const monthEndDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const monthStart = toIsoDate(monthStartDate);
    const monthEnd = toIsoDate(monthEndDate);
    const existing = monthMap.get(monthStart);
    if (existing) return existing;
    const item = { monthStart, monthEnd, incomeSummaries: [], expenses: [] };
    monthMap.set(monthStart, item);
    return item;
  }

  for (const income of incomeSummaries) {
    ensureMonth(incomePeriodDate(income)).incomeSummaries.push(income);
  }
  for (const expense of expenses) {
    ensureMonth(expense.date).expenses.push(expense);
  }

  return Array.from(monthMap.values())
    .map((month) => {
      const incomeTotal = roundMoney(month.incomeSummaries.reduce((sum, income) => sum + income.grossIncome, 0));
      const expenseTotal = roundMoney(month.expenses.reduce((sum, expense) => sum + expense.amount, 0));
      const platformFeeCredits = roundMoney(month.incomeSummaries.reduce((sum, income) => sum + (income.platformFeesGst || 0), 0));
      const expenseCredits = roundMoney(month.expenses.reduce((sum, expense) => sum + claimableGstForExpense(expense), 0));
      const gstOnSales = gstFromGross(incomeTotal);
      const gstCredits = roundMoney(platformFeeCredits + expenseCredits);
      const date = new Date(`${month.monthStart}T00:00:00`);
      return {
        id: month.monthStart.slice(0, 7),
        label: date.toLocaleDateString("en-AU", { month: "short", year: "numeric" }),
        monthStart: month.monthStart,
        monthEnd: month.monthEnd,
        incomeTotal,
        expenseTotal,
        gstOnSales,
        gstCredits,
        platformFeeCredits,
        expenseCredits,
        netGst: roundMoney(gstOnSales - gstCredits),
        incomeSummaries: month.incomeSummaries,
        expenses: month.expenses,
      };
    })
    .sort((a, b) => b.monthStart.localeCompare(a.monthStart));
}

export function basSummaryToCsv(summary: BasSummary) {
  const rows = [
    ["BAS period", summary.period.label],
    ["Start date", summary.period.startDate],
    ["End date", summary.period.endDate],
    ["Due date", summary.period.dueDate],
    ["G1 Total sales", summary.g1TotalSales.toFixed(2)],
    ["1A GST on sales", summary.gstOnSales1A.toFixed(2)],
    ["1B GST on purchases", summary.gstOnPurchases1B.toFixed(2)],
    ["Net GST payable/refundable", summary.netGst.toFixed(2)],
    [],
    ["Source", "Amount"],
    ["Platform gross fares", summary.sourceSales.toFixed(2)],
    ["Deductions GST credits", summary.sourcePlatformCredits.toFixed(2)],
    ["Manual expense GST credits", summary.manualExpenseCredits.toFixed(2)],
  ];

  return rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
}

export function annualTaxSummaryToCsv(summary: AnnualTaxSummary) {
  const selectedMethod = summary.settings.selectedVehicleMethod;
  const methodLabel = annualVehicleMethodLabel(selectedMethod);
  const rideshareIncomeNet = roundMoney(summary.rideshareIncome - summary.incomeGstExcluded);
  const platformGstCredits = roundMoney(summary.incomeSummaries.reduce((sum, income) => sum + (income.platformFeesGst || 0), 0));
  const operatingExpenseGstCredits = roundMoney(summary.expenses
    .filter((expense) => expense.category !== "car_purchase")
    .reduce((sum, expense) => sum + claimableGstForExpense(expense), 0));
  const potentialVehiclePurchaseGstCredit = roundMoney(summary.expenses
    .filter((expense) => expense.category === "car_purchase")
    .reduce((sum, expense) => sum + claimableGstForExpense(expense), 0));
  const expenseGstCredits = roundMoney(operatingExpenseGstCredits + potentialVehiclePurchaseGstCredit);
  const vehiclePurchase = annualVehiclePurchaseDetails(summary);
  const vehicleRunningDeduction = selectedMethod === "logbook_actual" ? summary.selectedVehicleDeduction : 0;
  const centsPerKmDeduction = selectedMethod === "cents_per_km" ? summary.selectedVehicleDeduction : 0;
  const totalVehicleDeduction = roundMoney(summary.selectedVehicleDeduction + summary.includedDepreciationDeductions);
  const gstRegistered = summary.incomeSummaries.some((income) => income.provider === "uber" || income.provider === "didi") ? "Yes" : "Not supplied";
  const vehicleMethodNote =
    selectedMethod === "cents_per_km"
      ? "Cents-per-km includes vehicle running costs and depreciation; those items are not separately deducted."
      : "Logbook/actual cost method allows running costs plus reviewed depreciation where applicable.";
  const rows: Array<Array<string | number>> = [
    ["Income Summary"],
    ["Annual tax worksheet", summary.period.label],
    ["Start date", summary.period.startDate],
    ["End date", summary.period.endDate],
    ["Assessable income", summary.assessableIncome.toFixed(2)],
    ["Rideshare income GST-inclusive", summary.rideshareIncome.toFixed(2)],
    ["Rideshare income net of GST", rideshareIncomeNet.toFixed(2)],
    ["Tips and bonuses", summary.tipsAndBonuses.toFixed(2)],
    ["Other income adjustments", summary.adjustmentIncome.toFixed(2)],
    [],
    ["GST Treatment"],
    ["GST excluded from rideshare income", summary.incomeGstExcluded.toFixed(2), "GST on rideshare fares is excluded from annual assessable income."],
    ["Platform fee GST credits", platformGstCredits.toFixed(2), "GST credits already claimed should not be deducted again for income tax."],
    ["Operating expense GST credits", operatingExpenseGstCredits.toFixed(2), "GST credits already claimed in BAS are excluded from income-tax deductions."],
    ["Potential vehicle purchase GST credit", potentialVehiclePurchaseGstCredit.toFixed(2), "Accountant review required; passenger car GST credits may be capped by the car limit and business-use rules."],
    ["Vehicle purchase GST accountant review required", potentialVehiclePurchaseGstCredit > 0 ? "Yes" : "No"],
    ["Vehicle purchase GST review reason", potentialVehiclePurchaseGstCredit > 0 ? "Passenger car GST credit may be capped by car limit." : "No vehicle purchase GST credit recorded."],
    ["Total GST credits", expenseGstCredits.toFixed(2), "Operating expense GST credits plus potential vehicle purchase GST credit."],
    [],
    ["Deduction Summary"],
    ["Deduction", "Included", "Count", "Amount", "Note"],
    ["Platform fees net of GST", "Yes", summary.incomeSummaries.filter((income) => income.platformFeesGross > 0).length, summary.platformFeeDeduction.toFixed(2), "GST credits already claimed are excluded."],
    ["Vehicle running costs", selectedMethod === "logbook_actual" ? "Yes" : "No", summary.expenses.filter((expense) => isVehicleRunningCost(expense.category)).length, summary.vehicleRunningCosts.toFixed(2), selectedMethod === "cents_per_km" ? "Excluded because cents-per-km is selected." : "Included under logbook/actual method."],
    ["Depreciation / annual adjustment", summary.includedDepreciationDeductions > 0 ? "Yes" : "No", summary.adjustments.filter((adjustment) => adjustment.type === "car_depreciation" || adjustment.type === "instant_asset_write_off").length, summary.includedDepreciationDeductions.toFixed(2), selectedMethod === "cents_per_km" ? "Excluded because cents-per-km includes depreciation." : "Vehicle purchase is not automatically fully deductible; depreciation may be subject to the car limit."],
    ["General business expenses", "Yes", summary.expenses.filter((expense) => !isVehicleRunningCost(expense.category) && expense.category !== "car_purchase").length, summary.otherExpenseDeductions.toFixed(2), "Non-vehicle business expenses remain separately deductible where supported."],
    ["Other deductions", summary.otherAnnualDeductions > 0 ? "Yes" : "No", summary.adjustments.filter((adjustment) => adjustment.type === "other_deduction").length, summary.otherAnnualDeductions.toFixed(2), "Other annual-only deduction adjustments."],
    ["Total deductions", "Yes", "", summary.totalDeductions.toFixed(2), "Reconciles to transaction detail deductible amounts."],
    [],
    ["Vehicle Summary"],
    ["Vehicle make/model", vehiclePurchase.makeModel],
    ["Purchase date", vehiclePurchase.purchaseDate],
    ["Vehicle purchase price (GST-inclusive)", vehiclePurchase.invoicePrice],
    ["Vehicle invoice cost GST-exclusive", vehiclePurchase.invoicePriceExGst],
    ["Registration / CTP / on-road costs total", vehiclePurchase.onRoadCosts],
    ["Total acquisition-related payments", vehiclePurchase.totalAcquisitionCosts],
    ["Potential GST credit on vehicle purchase", vehiclePurchase.gstClaimed],
    ["Accountant review required", vehiclePurchase.reviewRequired],
    ["Review reason", vehiclePurchase.reviewReason],
    ["Business use percentage", percentOrNotProvided(summary.settings.logbookBusinessUsePercent)],
    ["Business use source", selectedMethod === "logbook_actual" ? "User-entered logbook / business-use percentage" : "Cents-per-km business kilometres"],
    ["Vehicle deduction method", methodLabel],
    ["Business kilometres", numberOrNotProvided(summary.settings.businessKilometres)],
    ["Total kilometres", numberOrNotProvided(summary.settings.totalKilometres)],
    ["Cents per km rate", summary.settings.centsPerKmRate.toFixed(2)],
    ["Vehicle running costs deduction", vehicleRunningDeduction.toFixed(2)],
    ["Cents-per-km vehicle deduction", centsPerKmDeduction.toFixed(2)],
    ["Depreciation deduction", summary.includedDepreciationDeductions.toFixed(2)],
    ["Total vehicle deduction", totalVehicleDeduction.toFixed(2), vehicleMethodNote],
    [],
    ["Tax Method Summary"],
    ["GST registered", gstRegistered],
    ["Vehicle deduction method", methodLabel],
    ["Business use percentage", percentOrNotProvided(summary.settings.logbookBusinessUsePercent)],
    ["Business kilometres", numberOrNotProvided(summary.settings.businessKilometres)],
    ["Total kilometres", numberOrNotProvided(summary.settings.totalKilometres)],
    ["Depreciation included", summary.includedDepreciationDeductions > 0 ? "Yes" : "No"],
    ["Running costs included", selectedMethod === "logbook_actual" ? "Yes" : "No"],
    ["Cents-per-km cap applied", selectedMethod === "cents_per_km" && summary.settings.businessKilometres > BUSINESS_KM_CAP ? "Yes" : "No"],
    ["Generated at (export time)", generatedTimestamp()],
    [],
    ["Net Business Income"],
    ["Assessable income", summary.assessableIncome.toFixed(2)],
    ["Total deductions", summary.totalDeductions.toFixed(2)],
    ["Net business income", summary.netBusinessProfit.toFixed(2)],
    [],
    ["Reconciliation"],
    ["Income Reconciliation"],
    ["Rideshare income net of GST", rideshareIncomeNet.toFixed(2)],
    ["Tips and bonuses", summary.tipsAndBonuses.toFixed(2)],
    ["Other income adjustments", summary.adjustmentIncome.toFixed(2)],
    ["Assessable income", summary.assessableIncome.toFixed(2)],
    [],
    ["Deduction Reconciliation"],
    ["Platform fees net of GST", summary.platformFeeDeduction.toFixed(2)],
    ["Vehicle deduction", totalVehicleDeduction.toFixed(2)],
    ["General business expenses", summary.otherExpenseDeductions.toFixed(2)],
    ["Other deductions", summary.otherAnnualDeductions.toFixed(2)],
    ["Total deductions", summary.totalDeductions.toFixed(2)],
    [],
    ["Transaction Detail"],
    ["Date", "Description", "Merchant", "Category", "Subcategory", "GST-inclusive amount", "GST amount", "Claimable GST", "GST-exclusive amount", "Business use %", "Deductible amount", "Tax treatment", "Source", "Evidence / receipt reference", "Notes"],
    ...annualTransactionRows(summary),
    [],
    ["Accountant Notes"],
    ["This report is a tax working paper, not a lodged tax return."],
    ["Amounts are based on user-entered records and imported platform data."],
    ["GST credits already claimed should not be deducted again for income tax."],
    ["Vehicle purchase is not automatically fully deductible."],
    ["Depreciation may be subject to the car limit."],
    ["Business-use percentage should be supported by a compliant logbook where required."],
    ["Cents-per-km method includes running costs and depreciation."],
    ["Logbook/actual cost method allows running costs plus reviewed depreciation."],
    ["Registration and CTP GST should be verified against the tax invoice because these payments can include GST-free, duty, levy, or other non-creditable components."],
    [],
    ["Warnings"],
    ["Warning", "Severity", "Message"],
    ...summary.auditNotes.map((note) => [note.title, note.severity, note.message]),
  ];

  return rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
}

function annualTransactionRows(summary: AnnualTaxSummary): Array<Array<string | number>> {
  const selectedMethod = summary.settings.selectedVehicleMethod;
  const rows: Array<Array<string | number>> = [];

  for (const income of summary.incomeSummaries) {
    if (income.platformFeesGross <= 0) continue;
    const gst = roundMoney(income.platformFeesGst || 0);
    const deductible = roundMoney(Math.max(0, income.platformFeesGross - gst));
    rows.push([
      income.periodEnd || income.periodStart,
      "Platform fees net of GST",
      income.provider === "uber" ? "Uber" : income.provider === "didi" ? "DiDi" : income.provider === "manual" ? "Manual" : "Rideshare",
      "Platform fees",
      "Rideshare income summary",
      income.platformFeesGross.toFixed(2),
      gst.toFixed(2),
      gst.toFixed(2),
      deductible.toFixed(2),
      formatPercent(100),
      deductible.toFixed(2),
      "Deductible",
      annualIncomeSourceLabel(income),
      income.sourceDocumentId || "",
      "Platform fee GST credits already excluded from income-tax deduction.",
    ]);
  }

  if (selectedMethod === "cents_per_km" && summary.selectedVehicleDeduction > 0) {
    rows.push([
      summary.period.endDate,
      "Cents-per-km vehicle deduction",
      "",
      "Vehicle",
      annualVehicleMethodLabel("cents_per_km"),
      summary.selectedVehicleDeduction.toFixed(2),
      "0.00",
      "0.00",
      summary.selectedVehicleDeduction.toFixed(2),
      formatPercent(100),
      summary.selectedVehicleDeduction.toFixed(2),
      "Deductible",
      "Annual tax setting",
      "",
      "Capped at 5,000 business kilometres.",
    ]);
  }

  for (const expense of summary.expenses) {
    const gst = expenseGstComponent(expense);
    const businessGstCredit = claimableGstForExpense(expense);
    const isVehicle = isVehicleRunningCost(expense.category);
    let deductible = expenseIncomeTaxDeduction(expense, isVehicle ? vehicleExpenseBusinessUsePercent(expense) : undefined);
    let notes = appendRegistrationCtpNote(expense, expense.notes || "");
    if (isVehicle && selectedMethod === "cents_per_km") {
      deductible = 0;
      notes = `${notes} Covered by cents-per-km method; not separately deducted.`.trim();
    }
    if (expense.category === "car_purchase") {
      deductible = 0;
      notes = `${notes} Vehicle purchase is not automatically fully deductible; review depreciation/car limit.`.trim();
    }
    rows.push([
      expense.date,
      expense.description || expenseCategoryLabels[expense.category],
      expense.supplier || "",
      expenseCategoryLabels[expense.category],
      expense.category === "car_purchase" ? "Vehicle purchase" : isVehicle ? "Vehicle running cost" : "General business expense",
      expense.amount.toFixed(2),
      gst.toFixed(2),
      businessGstCredit.toFixed(2),
      Math.max(0, roundMoney(expense.amount - gst)).toFixed(2),
      formatPercent(expense.businessUsePercent || 0),
      deductible.toFixed(2),
      annualExpenseTaxTreatment(expense, deductible, businessGstCredit),
      annualExpenseSourceLabel(expense),
      expense.sourceDocumentId || "",
      notes,
    ]);
  }

  for (const adjustment of summary.adjustments) {
    if (adjustment.type === "other_income") continue;
    const isDepreciation = adjustment.type === "car_depreciation" || adjustment.type === "instant_asset_write_off";
    let deductible = adjustment.amount;
    let notes = isDepreciation ? "Depreciation may be subject to the car limit." : "Annual-only deduction adjustment.";
    if (isDepreciation && selectedMethod === "cents_per_km") {
      deductible = 0;
      notes = "Covered by cents-per-km method; not separately deducted.";
    }
    rows.push([
      summary.period.endDate,
      adjustment.description || annualAdjustmentLabel(adjustment.type),
      "",
      isDepreciation ? "Vehicle" : "Annual adjustment",
      annualAdjustmentLabel(adjustment.type),
      adjustment.amount.toFixed(2),
      "0.00",
      "0.00",
      adjustment.amount.toFixed(2),
      formatPercent(100),
      deductible.toFixed(2),
      "Annual adjustment",
      "Manual annual adjustment",
      "",
      notes,
    ]);
  }

  return rows;
}

function annualAdjustmentLabel(type: AnnualTaxAdjustment["type"]) {
  if (type === "car_depreciation") return "Car depreciation";
  if (type === "instant_asset_write_off") return "Instant asset write-off";
  if (type === "other_deduction") return "Other deduction";
  return "Annual adjustment";
}

function annualIncomeSourceLabel(income: IncomeSummary) {
  if (income.sourceType === "manual") return "Manual entry";
  if (income.provider === "uber") return "Uber monthly statement";
  if (income.provider === "didi") return "DiDi monthly statement";
  return "Rideshare income summary";
}

function annualExpenseSourceLabel(expense: Expense) {
  if (expense.entrySource === "document_scan" || expense.sourceDocumentId) return "Receipt OCR";
  return "Manual entry";
}

function annualExpenseTaxTreatment(expense: Expense, deductible: number, claimableGst: number) {
  if (expense.category === "car_purchase") return "Capital";
  if (expense.businessUsePercent <= 0) return "Private";
  if (deductible > 0) return "Deductible";
  if (claimableGst > 0) return "GST only";
  return "Private";
}

function appendRegistrationCtpNote(expense: Expense, notes: string) {
  const text = `${expense.supplier} ${expense.description}`.toLowerCase();
  if (expense.category !== "registration" && !(expense.category === "insurance" && text.includes("ctp"))) {
    return notes;
  }
  const note = "Registration/CTP GST should be verified against the tax invoice.";
  return notes.includes(note) ? notes : `${notes} ${note}`.trim();
}

function generatedTimestamp() {
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Brisbane",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZoneName: "short",
  }).formatToParts(new Date());
  const value = (type: string) => parts.find((part) => part.type === type)?.value || "";
  return `${value("year")}-${value("month")}-${value("day")} ${value("hour")}:${value("minute")} ${value("timeZoneName")}`.trim();
}

function annualVehiclePurchaseDetails(summary: AnnualTaxSummary) {
  const carPurchases = summary.expenses
    .filter((expense) => expense.category === "car_purchase")
    .sort((a, b) => b.amount - a.amount);
  const primaryPurchase = carPurchases[0];
  const purchaseTotal = roundMoney(carPurchases.reduce((sum, expense) => sum + expense.amount, 0));
  const purchaseGstCredit = roundMoney(carPurchases.reduce((sum, expense) => sum + claimableGstForExpense(expense), 0));
  const primaryPurchaseAmount = roundMoney(primaryPurchase?.amount || 0);
  const makeModelSetting = textOrNotProvided(summary.settings.vehicleMakeModel);
  const purchaseDateSetting = textOrNotProvided(summary.settings.vehiclePurchaseDate);
  const makeModel = makeModelSetting !== "Not provided"
      ? makeModelSetting
      : primaryPurchase
        ? textOrNotProvided(primaryPurchase.description || primaryPurchase.supplier)
        : "Not provided";
  const purchaseDate = purchaseDateSetting !== "Not provided"
      ? purchaseDateSetting
      : primaryPurchase
        ? textOrNotProvided(primaryPurchase.date)
        : "Not provided";
  const onRoadCosts = annualOnRoadCosts(summary, purchaseDate, purchaseTotal, primaryPurchaseAmount);
  const totalAcquisitionCosts = summary.settings.vehiclePurchasePrice > 0
    ? roundMoney(summary.settings.vehiclePurchasePrice + onRoadCosts)
    : roundMoney(primaryPurchaseAmount + onRoadCosts);
  const hasPurchase = summary.settings.vehiclePurchasePrice > 0 || totalAcquisitionCosts > 0;
  const reviewRequired = purchaseGstCredit > 0 || hasPurchase;
  const invoicePrice = summary.settings.vehiclePurchasePrice > 0 ? summary.settings.vehiclePurchasePrice : primaryPurchaseAmount;
  const invoiceGst = summary.settings.vehiclePurchaseGstClaimed > 0
    ? summary.settings.vehiclePurchaseGstClaimed
    : primaryPurchase
      ? expenseGstComponent(primaryPurchase)
      : 0;

  return {
    makeModel,
    purchaseDate,
    invoicePrice: vehiclePurchasePriceLabel(invoicePrice),
    invoicePriceExGst: invoicePrice > 0 ? Math.max(0, roundMoney(invoicePrice - invoiceGst)).toFixed(2) : vehiclePurchasePriceLabel(0),
    onRoadCosts: onRoadCosts > 0 ? onRoadCosts.toFixed(2) : "Not provided",
    totalAcquisitionCosts: totalAcquisitionCosts > 0 ? totalAcquisitionCosts.toFixed(2) : vehiclePurchasePriceLabel(0),
    gstClaimed: summary.settings.vehiclePurchaseGstClaimed > 0
      ? summary.settings.vehiclePurchaseGstClaimed.toFixed(2)
      : moneyOrNotProvided(purchaseGstCredit),
    reviewRequired: reviewRequired ? "Yes" : "No",
    reviewReason: reviewRequired
      ? "Passenger car GST credit may be capped by car limit."
      : "No vehicle purchase recorded this financial year.",
  };
}

function annualOnRoadCosts(summary: AnnualTaxSummary, purchaseDate: string, carPurchaseTotal: number, primaryPurchaseAmount: number) {
  const fromCarPurchaseRows = roundMoney(Math.max(0, carPurchaseTotal - primaryPurchaseAmount));
  if (purchaseDate === "Not provided") return fromCarPurchaseRows;
  const relatedRunningCosts = summary.expenses.reduce((sum, expense) => {
    if (expense.date !== purchaseDate) return sum;
    const text = `${expense.supplier} ${expense.description}`.toLowerCase();
    const isRelated = expense.category === "registration" || (expense.category === "insurance" && text.includes("ctp"));
    return isRelated ? sum + expense.amount : sum;
  }, 0);
  return roundMoney(fromCarPurchaseRows + relatedRunningCosts);
}

function annualVehicleMethodLabel(method: VehicleExpenseMethod) {
  return method === "cents_per_km" ? "Cents per Kilometre Method" : "Logbook (Actual Cost Method)";
}

function textOrNotProvided(value: string) {
  const text = value.trim();
  return text || "Not provided";
}

function numberOrNotProvided(value: number) {
  return value > 0 ? String(Number(value.toFixed(2))) : "Not provided";
}

function percentOrNotProvided(value: number) {
  return value > 0 ? formatPercent(value) : "Not provided";
}

function moneyOrNotProvided(value: number) {
  return value > 0 ? value.toFixed(2) : "Not provided";
}

function vehiclePurchasePriceLabel(value: number) {
  return value > 0 ? value.toFixed(2) : "No vehicle purchase recorded this financial year";
}

function formatPercent(value: number) {
  const fixed = value.toFixed(2).replace(/\.?0+$/, "");
  return `${fixed || "0"}%`;
}
